import type { RefObject } from "react";
import type {
  PendingScrollRequest,
  ScrollTargetOptions,
} from "./scroll-controller-types";

interface MessageRegistrationRefs {
  messageElementsRef: RefObject<Map<string, HTMLElement>>;
  elementToMessageIdRef: RefObject<WeakMap<HTMLElement, string>>;
  visibleMessageIdsRef: RefObject<Set<string>>;
  observerRef: RefObject<IntersectionObserver | null>;
  pendingScrollRef: RefObject<PendingScrollRequest | null>;
}

interface RegisterMessageElementInput extends MessageRegistrationRefs {
  messageId: string;
  el: HTMLElement | null;
  scrollToMessageId: (messageId: string, opts?: ScrollTargetOptions) => void;
}

interface RegisterVersionElementInput {
  versionShortId: string;
  el: HTMLElement | null;
  versionElementsRef: RefObject<Map<string, HTMLElement>>;
  pendingScrollRef: RefObject<PendingScrollRequest | null>;
  scrollToVersionId: (versionShortId: string, opts?: ScrollTargetOptions) => void;
}

export function registerMessageElement({
  messageId,
  el,
  messageElementsRef,
  elementToMessageIdRef,
  visibleMessageIdsRef,
  observerRef,
  pendingScrollRef,
  scrollToMessageId,
}: RegisterMessageElementInput): void {
  detachExistingMessageElement({
    messageId,
    messageElementsRef,
    visibleMessageIdsRef,
    observerRef,
  });

  if (!el) {
    messageElementsRef.current.delete(messageId);
    return;
  }

  messageElementsRef.current.set(messageId, el);
  elementToMessageIdRef.current.set(el, messageId);
  observerRef.current?.observe(el);
  flushPendingMessageScroll({ messageId, pendingScrollRef, scrollToMessageId });
}

export function registerVersionElement({
  versionShortId,
  el,
  versionElementsRef,
  pendingScrollRef,
  scrollToVersionId,
}: RegisterVersionElementInput): void {
  if (!el) {
    versionElementsRef.current.delete(versionShortId);
    return;
  }

  versionElementsRef.current.set(versionShortId, el);
  flushPendingVersionScroll({
    versionShortId,
    pendingScrollRef,
    scrollToVersionId,
  });
}

function detachExistingMessageElement({
  messageId,
  messageElementsRef,
  visibleMessageIdsRef,
  observerRef,
}: Pick<
  MessageRegistrationRefs,
  "messageElementsRef" | "visibleMessageIdsRef" | "observerRef"
> & {
  messageId: string;
}): void {
  const existing = messageElementsRef.current.get(messageId) ?? null;
  if (!existing || !observerRef.current) return;

  observerRef.current.unobserve(existing);
  visibleMessageIdsRef.current.delete(messageId);
}

function flushPendingMessageScroll({
  messageId,
  pendingScrollRef,
  scrollToMessageId,
}: {
  messageId: string;
  pendingScrollRef: RefObject<PendingScrollRequest | null>;
  scrollToMessageId: (messageId: string, opts?: ScrollTargetOptions) => void;
}): void {
  const pending = pendingScrollRef.current;
  if (pending?.targetMessageId !== messageId) return;

  pendingScrollRef.current = null;
  scrollToMessageId(messageId, {
    instant: pending.instant,
    offsetTop: pending.offsetTop,
  });
}

function flushPendingVersionScroll({
  versionShortId,
  pendingScrollRef,
  scrollToVersionId,
}: {
  versionShortId: string;
  pendingScrollRef: RefObject<PendingScrollRequest | null>;
  scrollToVersionId: (versionShortId: string, opts?: ScrollTargetOptions) => void;
}): void {
  const pending = pendingScrollRef.current;
  if (pending?.targetVersionId !== versionShortId) return;

  pendingScrollRef.current = null;
  scrollToVersionId(versionShortId, {
    instant: pending.instant,
    offsetTop: pending.offsetTop,
  });
}
